import React, { useState } from 'react';
import { User, LogOut, CreditCard, Settings, Crown } from 'lucide-react';
import { useAuth } from '../../lib/auth';
import { useBilling, useUsageTracking } from '../../lib/billing';
import { AUTH_CONFIG, BILLING_CONFIG } from '../../config';
import { api } from '../../utils/api';
import { trackPaidCtaClicked } from '../../utils/billingFunnel';

const UserMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { user, signOut, isAuthenticated } = useAuth();
  const { currentPlan, getPlanInfo, plans } = useBilling();
  const { getQuotaInfo } = useUsageTracking();

  if (!AUTH_CONFIG.ENABLED || !isAuthenticated || !user) {
    return null;
  }

  const quotaInfo = getQuotaInfo(currentPlan);
  const planInfo = getPlanInfo(currentPlan);

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Sign out failed:', error);
    }
  };

  const handleBillingClick = () => {
    const targetPlan = plans.find(plan => plan.id !== currentPlan)?.id || 'paid';
    void trackPaidCtaClicked(api.trackFunnelEvent.bind(api), {
      userId: user.id,
      currentPlan,
      targetPlan,
      surface: 'user_menu',
    });
  };

  return (
    <div className="relative">
      {/* User Avatar/Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-100 transition-colors"
      >
        <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
          <User className="w-4 h-4 text-white" />
        </div>
        <div className="hidden md:block text-left">
          <div className="text-sm font-medium text-gray-900">
            {user.display_name || user.username || user.email}
          </div>
          {BILLING_CONFIG.ENABLED && (
            <div className="text-xs text-gray-500 flex items-center space-x-1">
              {currentPlan !== 'free' && <Crown className="w-3 h-3" />}
              <span className="capitalize">{currentPlan} Plan</span>
            </div>
          )}
        </div>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />

          {/* Menu */}
          <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-20">
            {/* User Info */}
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
                  <User className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <div className="font-medium text-gray-900">
                    {user.display_name || user.username}
                  </div>
                  <div className="text-sm text-gray-500">{user.email}</div>
                  {BILLING_CONFIG.ENABLED && (
                    <div className="flex items-center space-x-1 mt-1">
                      {currentPlan !== 'free' && <Crown className="w-3 h-3 text-yellow-500" />}
                      <span className="text-xs font-medium text-gray-600 capitalize">
                        {currentPlan} Plan
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Usage Stats (if billing enabled) */}
            {BILLING_CONFIG.ENABLED && BILLING_CONFIG.UI.SHOW_USAGE_METER && (
              <div className="p-4 border-b border-gray-200">
                <div className="text-sm font-medium text-gray-900 mb-2">Usage This Month</div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Requests</span>
                    <span className="font-medium">
                      {quotaInfo.monthlyUsed} / {quotaInfo.monthlyLimit === Infinity ? '∞' : quotaInfo.monthlyLimit}
                    </span>
                  </div>
                  {quotaInfo.monthlyLimit !== Infinity && (
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                        style={{
                          width: `${Math.min(100, (quotaInfo.monthlyUsed / quotaInfo.monthlyLimit) * 100)}%`
                        }}
                      />
                    </div>
                  )}
                  {currentPlan === 'free' && quotaInfo.monthlyRemaining <= 10 && (
                    <div className="text-xs text-orange-600 font-medium">
                      Only {quotaInfo.monthlyRemaining} requests remaining this month
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Menu Items */}
            <div className="py-2">
              {BILLING_CONFIG.ENABLED && (
                <button
                  onClick={handleBillingClick}
                  className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <CreditCard className="w-4 h-4" />
                  <span>Billing & Subscription</span>
                </button>
              )}
              <button className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                <Settings className="w-4 h-4" />
                <span>Settings</span>
              </button>
            </div>

            {/* Sign Out */}
            <div className="border-t border-gray-200 py-2">
              <button
                onClick={handleSignOut}
                className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span>Sign Out</span>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default UserMenu;
